import { getProductByID } from '@adornis/buchungsheld-db/api/product.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCurrentUserServer, loadRawEntityDataByIDs } from '@adornis/bh-util/util.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { _updateParticipationStateAsAdminUser } from '../participation.js';

export const updateParticipationsStateAsAdminUser = registerMutation({
  operationName: 'updateParticipationsStateAsAdminUser',
  type: () => Int,
  params: [
    { name: 'participationIDs', type: () => [String] },
    { name: 'state', type: () => String },
  ],
  resolve: (participationIDs: string[], nextState: ParticipationState) => async () => {
    if (participationIDs.length === 0) return 0;
    const currentUser = await getCurrentUserServer();
    if (!currentUser) throw new Error('not logged in');

    const productGqlFields = {
      _id: 1,
      _class: 1,
      institutionID: 1,
      settings: { blockSignOffDaysInAdvance: 1, inheritedBlockSignOffDaysInAdvance: 1 },
    } as const;
    const participations = await loadRawEntityDataByIDs(Participation, participationIDs, {
      _id: 1,
      _class: 1,
      state: 1,
      userID: 1,
      productID: 1,
      timeslotID: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      waitingListPlace: 1,
      nachrueckPriority: 1,
      product: productGqlFields,
    });

    const productID = participations[0]!.productID;
    const product = await getProductByID(productID)(productGqlFields);
    if (!product) throw new Error('product not found ' + productID);
    const institutionID = product.institutionID;

    if (!currentUser.hasPermission(Permission.CHANGE_PARTICIPATION_STATE, institutionID)) {
      throw new Error('You dont have enough permission!');
    }

    // check if all participations are from the same product
    for (const participation of participations) {
      if (participation.productID !== productID) {
        throw new Error(
          'all participations must be from the same product. Got ' + productID + ' and ' + participation.productID,
        );
      }

      // avoid fetching the same product multiple times
      participation.product = product;
    }

    // check persmissions
    for (const participation of participations) {
      const currentState = participation.state;
      if (nextState === currentState) continue;

      const match = (inp1, inp2) => {
        const inp1Matches = inp1 === currentState || (Array.isArray(inp1) && inp1.includes(currentState));
        const inp2Matches = inp2 === nextState || (Array.isArray(inp2) && inp2.includes(nextState));
        return inp1Matches && inp2Matches;
      };

      switch (true) {
        // participated
        case match(ParticipationState.waiting, ParticipationState.participated):
        case match(ParticipationState.left, ParticipationState.participated):
        case match(ParticipationState.in, ParticipationState.participated):
        case match(ParticipationState.suspended, ParticipationState.participated):
        case match(ParticipationState.no_show, ParticipationState.participated):
          if (
            !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.CHANGE_PARTICIPATION_STATE, institutionID)
          )
            throw new ExpectedError('You dont have enough permission!');
          break;

        // not participated
        case match(ParticipationState.participated, ParticipationState.in):
          if (
            !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.CHANGE_PARTICIPATION_STATE, institutionID)
          )
            throw new ExpectedError('You dont have enough permission!');
          break;

        // no show
        case match(ParticipationState.no_show, ParticipationState.in):
        case match(Object.values(ParticipationState), ParticipationState.no_show): // any state to no show is an admin action
        case match(ParticipationState.no_show, ParticipationState.left):
          if (
            !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.CHANGE_PARTICIPATION_STATE, institutionID)
          )
            throw new ExpectedError('You dont have enough permission!');
          break;

        case match(ParticipationState.waiting, ParticipationState.in): // waitinglist nachruecken
        case match(ParticipationState.left, ParticipationState.in): // reparticipate
        case match(ParticipationState.left, ParticipationState.waiting): // back to warteliste
        case match(ParticipationState.participated, ParticipationState.left): // abgemeldet
        case match(ParticipationState.in, ParticipationState.left):
        case match(ParticipationState.participated, ParticipationState.waiting): // abhemeldet 2
        case match(ParticipationState.in, ParticipationState.waiting):
        case match([ParticipationState.waiting], ParticipationState.left): // leaving the waitinglist
        case match([ParticipationState.suspended], ParticipationState.left): // ingnore these abmeldungen
        case match(ParticipationState.participated, ParticipationState.suspended): // suspension
        case match(ParticipationState.in, ParticipationState.suspended):
        case match(ParticipationState.waiting, ParticipationState.suspended):
        case match(ParticipationState.suspended, ParticipationState.in): // suspension cancled
        case match(ParticipationState.suspended, ParticipationState.waiting):
        case match(ParticipationState.suspended, ParticipationState.left):
        case match(ParticipationState.left, ParticipationState.suspended): // ignore these suspensions
        case match(ParticipationState.in, ParticipationState.in): // ingnore
        case match(ParticipationState.left, ParticipationState.left):
          break;

        default:
          throw new Error(
            'state transition not implemented in permission check matrix: ' + currentState + ' -> ' + nextState,
          );
      }
    }

    return _updateParticipationStateAsAdminUser(participations, nextState);
  },
});
